import { Core } from "@faire/design-tokens";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { optimizeImageUrl } from "@faire/web/common/transform-images";
import { Column } from "@faire/web/slate/Layout";
import { getSpacing } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import React, { PropsWithChildren } from "react";
import styled from "styled-components";

type Size = {
  width: number;
  height: number;
};

interface IProps {
  mobileOffset?: string;
  imageUrl?: string;
  size?: Size;
  loadTimingRef?: React.RefCallback<HTMLElement>;
}

export const UpsellContainer: React.FC<PropsWithChildren<IProps>> = ({
  imageUrl,
  size,
  mobileOffset,
  children,
  loadTimingRef,
}) => {
  const hasImage = imageUrl && size;
  return (
    <Container ref={loadTimingRef}>
      {hasImage ? <HeroImage imageUrl={imageUrl} size={size} /> : null}
      <ChildrenContainer $mobileOffset={mobileOffset} $fullyRounded={!hasImage}>
        {children}
      </ChildrenContainer>
    </Container>
  );
};

interface IHeroImageProps {
  imageUrl: string;
  size: Size;
}
const HeroImage: React.FC<IHeroImageProps> = ({ imageUrl, size }) => {
  const isSSR = useIsSSR();
  const imageSrc = optimizeImageUrl(
    IImage.build({ url: imageUrl }),
    isSSR,
    size
  );

  return (
    <Image src={imageSrc} $aspectRatio={`${size.width} / ${size.height}`} />
  );
};

const Container = styled(Column)`
  width: 100%;

  ${responsiveMediaQueries.tabletAndAbove} {
    max-width: 608px;
  }
`;

const Image = styled.img<{ $aspectRatio: string }>`
  width: 100%;
  aspect-ratio: ${({ $aspectRatio }) => $aspectRatio} auto;
  background-color: ${Core.surface.subdued};
  max-height: 280px;
  object-fit: cover;

  ${responsiveMediaQueries.tabletAndAbove} {
    border-radius: ${getSpacing("1x", "1x", 0, 0)};
  }
`;

const ChildrenContainer = styled(Column)<{
  $mobileOffset?: string;
  $fullyRounded?: boolean;
}>`
  background-color: ${Core.surface.inverse};
  padding: ${getSpacing("4x", "3x")};

  ${responsiveMediaQueries.mobile} {
    border-radius: ${getSpacing("1x")};
    margin: ${getSpacing(0, "2x")};
    margin-top: ${({ $mobileOffset: mobileOffset = 0 }) => mobileOffset};
  }

  ${responsiveMediaQueries.tabletAndAbove} {
    padding: ${getSpacing("8x")};
    border-radius: ${({ $fullyRounded: fullyRounded }) =>
      fullyRounded ? getSpacing("1x") : getSpacing(0, 0, "1x", "1x")};
  }
`;
